import { IMeetingInfo } from "@/common/entity/Entity";
import {
  EColorPermissionFloorPlan,
  ETheme,
  EThemeBackground,
} from "@/common/entity/Enum";
import { transformToTree } from "@/common/function/ArrayFunction";
import { E_SIZE } from "@/components/ui/Loading/Models";
import Nodata from "@/components/ui/Nodata";
import {
  ICategorizationInfo,
  ITreeInfo,
} from "@/components/ui/TreeSelect/Models";
import { RootState } from "@/redux";
import { IOrganizationStore } from "@/redux/slices/organizations/OrganizationModel";
import {
  IFilterResource,
  IFilterResourceRequest,
  TransitiveMemberOfGroup,
} from "@/services";
import { DateTimePeriod } from "@/services/organization-service/entity/OutLookEntity";
import {
  FloorPlanInfo,
  IFetchFloorPlanRequest,
} from "@/services/resource-service/entity/FloorplanEntity";
import { BaseWrapper } from "@/view/addin/resource-finder-initial/Styles";
import Filter from "@/view/dialog/floor-plan-dialog-view/floor-plan/filter";
import PrevAndNext from "@/view/dialog/floor-plan-dialog-view/floor-plan/next-prev-location";
import ResourceSelected from "@/view/dialog/selected-resources/ResourceSelectedContainer";
import {
  FluentProvider,
  Spinner,
  Theme,
  webDarkTheme,
  webLightTheme,
} from "@fluentui/react-components";
import { cloneDeep } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IFloorPlanProps } from "./Models";
import {
  FloorPlanBaseWrapper,
  FloorPlanContentWrapper,
  FloorPlanDialogWrapper,
  ResourceSelectedWrapper,
} from "./Styles";
import DrawFloorPlan from "./draw-floor-plan/DrawFloorPlanContainer";

const ResourceFloorPlan = (props: IFloorPlanProps) => {
  const { t } = useTranslation();
  const theme: string = useSelector(
    (state: RootState) => state.settingInfo?.theme
  ) as string;

  const state: RootState = useSelector(
    (state: RootState) => state
  ) as RootState;
  const orgStore: IOrganizationStore = state.orgInfo as IOrganizationStore;

  const locationList: ICategorizationInfo[] =
    state?.orgInfo?.locationList || [];
  const filter: IFilterResource | null = state?.resources?.filterResource;

  const [dataTreeLocation, setDataTreeLocation] = useState<ITreeInfo[]>([]);
  const [resourceFloorPlanRequest, setResourceFloorPlanRequest] =
    useState<IFetchFloorPlanRequest | null>(null);
  const [listLocationHaveFloorPlan, setListLocationHaveFloorPlan] = useState<
    string[]
  >([]);
  const [loadingNextPrev, setLoadingNextPrev] = useState<boolean>(false);
  const [floorPlanInfo, setFloorPlanInfo] = useState<FloorPlanInfo | null>(
    null
  );
  const [heightCanvas, setHeightCanvas] = useState<number>(0);
  const resourceDivRef = useRef(null);

  const { onFetchResourceFloorPlan } = props;

  useEffect(() => {
    const div = resourceDivRef?.current;
    if (!div) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (resourceDivRef.current) {
          const height = (resourceDivRef.current as HTMLElement).offsetHeight;
          const cal = height - 80;
          setHeightCanvas(cal);
        }
      }
    });
    observer.observe(div);
    return () => {
      observer.unobserve(div);
    };
  }, [resourceDivRef]);

  useEffect(() => {
    if (resourceFloorPlanRequest) {
      fetchResourceFloorPlanService(resourceFloorPlanRequest);
    }
  }, [resourceFloorPlanRequest]);

  useEffect(() => {
    const locationHaveFloorPlanClone: string[] = (
      locationFilterFloorplan() || []
    )
      .filter((item) => item.isUsedFloorPlan)
      .map((locationInfo) => locationInfo.guid);
    if (
      orgStore.orgInfo &&
      locationHaveFloorPlanClone.length &&
      orgStore.outlookInfo
    ) {
      const filterReq: IFilterResourceRequest | null = filter
        ? {
            CategoryIds: filter?.categoryIds?.length
              ? filter.categoryIds.join(` ${filter.categoryCondition || "or"} `)
              : null,
            ResourceTypes: filter.resourceTypes || null,
          }
        : null;
      fetchResourceFloorPlan(
        filterReq,
        locationHaveFloorPlanClone[filter?.indexLocation || 0]
      );
    }
  }, [JSON.stringify(filter), orgStore]);

  useEffect(() => {
    initLocationFloorPlan();
  }, [JSON.stringify(filter?.locationIds), JSON.stringify(locationList)]);

  const initLocationFloorPlan = () => {
    if (locationList.length) {
      const treeTempSort: ITreeInfo[] = transformToTree(
        locationFilterFloorplan(),
        "guid",
        "parentId"
      );
      const locationHaveFloorPlanClone: string[] = locationFilterFloorplan()
        .filter((item) => item.isUsedFloorPlan)
        .map((locationInfo) => locationInfo.guid);
      setDataTreeLocation(treeTempSort);
      setListLocationHaveFloorPlan(locationHaveFloorPlanClone);
    }
  };

  const fetchResourceFloorPlan = async (
    filters?: IFilterResourceRequest | null,
    locationId?: string
  ): Promise<void> => {
    if (
      orgStore.orgInfo &&
      orgStore.outlookInfo &&
      orgStore.organizer &&
      locationId
    ) {
      const meetingInfos: IMeetingInfo[] = orgStore.outlookInfo
        .isRecurringMaster
        ? (orgStore.outlookInfo?.occurrences || [])?.map(
            (item: DateTimePeriod) => {
              return {
                StartTime: item.start?.toISOString() || "",
                EndTime: item.end?.toISOString() || "",
                Guid: orgStore.outlookInfo?.uid || undefined,
              };
            }
          )
        : [
            {
              StartTime: orgStore.outlookInfo.start?.toISOString() || "",
              EndTime: orgStore.outlookInfo.end?.toISOString() || "",
              Guid: orgStore.outlookInfo?.uid || undefined,
            },
          ];

      const request: IFetchFloorPlanRequest = {
        orgId: orgStore.orgInfo.guid,
        tenantId: orgStore.orgInfo.tenantId,
        requestBody: {
          Organizer: orgStore.organizer.email,
          IsRecurring: !!orgStore.outlookInfo.isRecurringMaster,
          EmailGroups: orgStore.orgInfo.memberOfs.map(
            (item: TransitiveMemberOfGroup) => item.mail
          ),
          MeetingInfos: meetingInfos,
          Filters: filters || null,
          LocationId: locationId,
        },
      };
      setResourceFloorPlanRequest(request);
    }
  };

  const locationFilterFloorplan = (): ICategorizationInfo[] => {
    const locationListClone = cloneDeep(locationList);
    if (locationListClone.length && filter?.locationIds?.length) {
      return locationListClone.filter((item: ICategorizationInfo) =>
        filter?.locationIds?.includes(item.guid)
      );
    }
    return locationListClone;
  };

  const fetchResourceFloorPlanService = async (
    resourceRequest: IFetchFloorPlanRequest | null
  ): Promise<void> => {
    if (onFetchResourceFloorPlan && resourceRequest) {
      setLoadingNextPrev(true);
      const response = await onFetchResourceFloorPlan(resourceRequest);
      setFloorPlanInfo(response);
      setLoadingNextPrev(false);
    } else {
      setFloorPlanInfo(null);
    }
  };

  const renderThemFluentUi = (): Theme => {
    if (theme === ETheme.Dark) {
      webDarkTheme.colorNeutralBackground1 = EThemeBackground.Dark;
      return webDarkTheme;
    }
    return webLightTheme;
  };

  return (
    <FluentProvider theme={renderThemFluentUi()}>
      <FloorPlanDialogWrapper className="FloorPlanDialogWrapper" $theme={theme}>
        <FloorPlanBaseWrapper className="FloorPlanBaseWrapper" $theme={theme}>
          <FloorPlanContentWrapper
            className="FloorPlanContentWrapper"
            ref={resourceDivRef}
          >
            {loadingNextPrev ? (
              <Spinner size={E_SIZE.medium} style={{ height: "100vh" }} />
            ) : (
              <>
                <div className="floor-header inner">
                  {floorPlanInfo ? (
                    <DrawFloorPlan
                      floorPlanInfo={floorPlanInfo}
                      messageToParent={props.messageToParent}
                      maxHeight={heightCanvas}
                    />
                  ) : (
                    <>
                      <Nodata />
                      <br />
                    </>
                  )}
                </div>
                <div className="floor-box-status-container">
                  <div className="floor-box-status-header">
                    <div className="frame-427323071">
                      <div
                        className="rectangle-24312"
                        style={{
                          background: EColorPermissionFloorPlan.Available,
                        }}
                      ></div>
                      <div className="available">{t("Available")}</div>
                    </div>

                    <div className="frame-427323079">
                      <div
                        className="rectangle-243125"
                        style={{
                          background: EColorPermissionFloorPlan.Disable,
                        }}
                      ></div>
                      <div className="closed">{t("Unavailable")}</div>
                    </div>

                    <div className="frame-4273230762">
                      <div
                        className="rectangle-243122"
                        style={{
                          background: EColorPermissionFloorPlan.Selected,
                        }}
                      ></div>
                      <div className="selected">{t("Selected")}</div>
                    </div>

                    {/* <div className="frame-427323077">
                <div className="rectangle-243123"></div>
                <div className="own">Own</div>
              </div> */}
                  </div>
                </div>
              </>
            )}
          </FloorPlanContentWrapper>
        </FloorPlanBaseWrapper>
        <ResourceSelectedWrapper className="ResourceSelectedWrapper">
          <BaseWrapper className="BaseWrapper">
            <Filter />
            <PrevAndNext
              dataTreeLocation={dataTreeLocation}
              listLocationHaveFloorPlan={listLocationHaveFloorPlan}
              locationList={locationList}
              updateFilterResource={props.updateFilterResource}
            />
            <ResourceSelected
              resourceEmailSelected={props.resourceEmailSelected}
              messageToParent={props.messageToParent}
            />
          </BaseWrapper>
        </ResourceSelectedWrapper>
      </FloorPlanDialogWrapper>
    </FluentProvider>
  );
};

export default ResourceFloorPlan;
